import { create } from 'zustand'

import { ApiError, api } from '../api/client'
import type {
  BalanceResponse,
  GambleGuess,
  GambleOffer,
  GambleResponse,
  GameInfo,
  GameMode,
  GameSummary,
  LineWin,
  SpinResponse,
  TumbleSpin,
} from '../api/client'
import { session as playerSession, takeLaunchTokenFromUrl } from '../api/session'
import { loadSfx, playSfx, setSfxMuted, startSfxLoop, unloadSfx } from '../audio/sfx'
import { config } from '../config'
import { emit, isFramed, startBridge } from '../iframe/bridge'
import { log } from '../log'
import { fallbackTheme, loadTheme, loadThemes } from '../theme'
// The banner and the fanfare fire on one threshold — see ui/bigWinTiers.ts.
import { BIG_WIN_MULT } from '../ui/bigWinTiers'
import type { ResolvedTheme } from '../theme'

/**
 * Game state.
 *
 * The balance is NEVER computed here. Every server response carries the
 * final balanceCoins and we simply accept it. Local arithmetic of
 * "balance minus bet plus win" will sooner or later drift apart from the
 * server — and precisely at the moment the player notices it.
 *
 * The store knows nothing about the board mechanic: the animation is
 * supplied by the Pixi layer through Animators.
 *
 * There are two screens: the lobby (the list of games) and the game
 * itself. The wallet is shared — there is one player and many games, so
 * the balance is not reset when moving between demos.
 */

type Phase = 'boot' | 'login' | 'idle' | 'spinning' | 'error'
type Screen = 'lobby' | 'game'

/** ways: spin the reels to a window. `speed` is a playback rate — the reels always scroll. */
export type WaysAnimator = (window: string[][], opts?: { speed?: number }) => Promise<void>
/** tumble: play a chain per the contract; onFrameWin — the chain's accumulated win in reference coins. */
export type TumbleAnimator = (
  spin: TumbleSpin,
  cb: { onFrameWin: (refCoins: number) => void; onBombs: (bombSum: number, chainTotal: number) => void },
  opts?: { speed?: number },
) => Promise<void>

/** Event banner over the tumble board (trigger, retrigger, summary). */
export type BannerFn = (
  text: string,
  kind: 'free' | 'win' | 'bomb',
  opts?: { speed?: number },
) => Promise<void>

/** lines: light the winning lines the server reported (after the reels stopped). */
export type LinesAnimator = (
  lineWins: LineWin[],
  opts?: { speed?: number; cycle?: boolean },
) => Promise<void>

export interface Animators {
  ways?: WaysAnimator
  tumble?: TumbleAnimator
  lines?: LinesAnimator
  banner?: BannerFn
}

interface GameState {
  phase: Phase
  screen: Screen
  error: string | null
  /**
   * How this session was opened. Only 'password' gets a sign-out control:
   * a player the operator launched has nowhere of ours to sign out to, and
   * a dev session has nothing to sign out of.
   */
  authMethod: 'password' | null

  /** Lobby: cards for every game on the server. */
  games: GameSummary[]
  /** The server's default game — the one the autospin dev hook opens. */
  defaultGameId: string
  /** The game open right now. null in the lobby and while the info loads. */
  game: GameInfo | null
  /**
   * Styling of the open game. Never null: a game with no theme gets the
   * placeholder, so no consumer needs an "if there is no theme" branch.
   */
  theme: ResolvedTheme
  /** Themes of every lobby game — the same ones colour the card previews. */
  themes: Record<string, ResolvedTheme>
  /** The selected game — known immediately, before the full info arrives. */
  selectedGameId: string | null
  playerId: string | null
  balanceCoins: number
  currency: string
  allowedBets: number[]
  betCoins: number
  mode: GameMode

  lastRound: SpinResponse | null
  /** The win currently on display — accumulates during the round. */
  displayWin: number
  freeSpinsLeft: number
  /** The current free spin (1..N) — for the badge. */
  freeSpinIndex: number
  /**
   * True while the free-spins segment of a round is on screen — what the
   * music player crossfades on. Deliberately not derived from
   * `freeSpinsLeft`: that counter survives past the summary banner, and
   * the music must come back down WITH the banner, not on the next spin.
   */
  inFreeSpins: boolean
  /** The last applied bomb multiplier — for the badge. */
  lastBombSum: number
  /**
   * Persistent games (Gates class): the bonus-wide global multiplier as of
   * the last finished free spin — the counter shown for the whole bonus.
   * -1 = the game has no accumulator (badge hidden); 0 = bonus started, nothing collected yet.
   */
  globalMult: number

  /**
   * Risk Game (lines games). The offer is whatever the server last said —
   * from the spin response or from the last gamble step. null = no offer
   * shown (not a lines game, or nothing to gamble).
   */
  gambleOffer: GambleOffer | null
  /** The last gamble step — the card the panel shows. Cleared by a new spin. */
  lastGamble: GambleResponse | null
  /** A gamble request is in flight: spin and gamble buttons are locked. */
  gambleBusy: boolean

  /**
   * Autospin: rounds still to run automatically. 0 = off. The loop itself
   * lives in App (it needs the animators); the store only holds the counter
   * so the UI and the loop agree on the state.
   */
  autoSpinsLeft: number
  startAutoSpins: (count: number) => void
  stopAutoSpins: () => void

  /**
   * The Big Win overlay is on screen. The autospin loop waits for this to
   * clear: a celebration the player never got to look at is the same as no
   * celebration at all. Owned by the overlay, read by the loop in App.
   */
  celebrating: boolean
  setCelebrating: (on: boolean) => void

  /**
   * Turbo: the round plays FAST, not silently. The reels still scroll and
   * the win is still lit — both simply run at several times the pace, and
   * the per-line walk collapses into one combined flash. The Big Win
   * overlay is untouched: it is the payoff, not the padding.
   */
  turbo: boolean
  toggleTurbo: () => void

  /**
   * The game the operator bound to this session, from the launch token.
   *
   * Non-null means the player was sent into ONE game, not into our lobby:
   * the operator already has a lobby, and offering a second one inside the
   * iframe would let the player wander out of what the operator opened.
   * The internal lobby is hidden for the whole session in that case.
   */
  boundGameId: string | null
  /** Framed by an operator — "back" means "ask the lobby to close us". */
  framed: boolean

  /**
   * Master sound switch, shared by music and effects.
   *
   * In the store rather than in the toggle button because the operator's
   * lobby can mute us over postMessage when the game loses focus, and two
   * independent mute states would immediately disagree.
   */
  soundOn: boolean
  setSoundOn: (on: boolean) => void

  boot: () => Promise<void>
  /** Signs a player in on our own stand and boots the game. */
  signIn: (login: string, password: string) => Promise<void>
  /** Ends the session and reloads back to the form. */
  signOut: () => Promise<void>
  /** Open a demo: switches the screen at once, the info loads after. */
  openGame: (gameId: string) => Promise<void>
  /** Return to the lobby. Does nothing while a spin is running. */
  closeGame: () => void
  /** Re-read the balance from the server (the operator credited the account). */
  refreshBalance: () => Promise<void>
  setBet: (betCoins: number) => void
  setMode: (mode: GameMode) => void
  /** Round price in coins for the current bet and mode. */
  chargeFor: (mode?: GameMode) => number
  spin: (animators: Animators) => Promise<void>
  /** Risk Game: stake the current offer on a colour. Balance comes back from the server. */
  gambleDouble: (guess: GambleGuess) => Promise<void>
  /** Risk Game: take the current offer and close the gamble. */
  gambleCollect: () => Promise<void>
}



/**
 * Did any paying line actually run through a wild?
 *
 * The window is indexed [reel][row], the same order the line positions
 * use, so this is a lookup rather than a search.
 */
function paysWithWild(
  window: string[][],
  lineWins: readonly LineWin[],
  wildCodes: ReadonlySet<string>,
): boolean {
  return lineWins.some((win) =>
    win.positions.some((p) => wildCodes.has(window[p.reel]?.[p.row] ?? '')),
  )
}

/** Roughly the length of the card-flip effect, after which the verdict lands. */
const GAMBLE_REVEAL_MS = 450

function isBigWin(round: SpinResponse): boolean {
  return round.chargedCoins > 0 && round.winCoins / round.chargedCoins >= BIG_WIN_MULT
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Pitch steps for the spin-start harp, in semitones. Scale degrees of the
 * music's D major (D, E, F#, A, B around the root), so whatever the wheel
 * of fortune picks stays consonant with the background loop — random
 * chromatic pitches would land a tritone off the pad once a minute.
 */
const SPIN_PITCHES = [-3, -1, 0, 2, 4, 7]
let lastSpinPitch = 0

/** A different consonant pitch each spin — never the same one twice. */
function nextSpinPitch(): number {
  let i = Math.floor(Math.random() * SPIN_PITCHES.length)
  if (SPIN_PITCHES[i] === lastSpinPitch) i = (i + 1) % SPIN_PITCHES.length
  lastSpinPitch = SPIN_PITCHES[i]!
  return 2 ** (lastSpinPitch / 12)
}

/** Reel playback rate in turbo — fast enough to feel instant, still a spin. */
const TURBO_SPIN_SPEED = 3.5
/** The win reveal in turbo: one combined flash instead of the line walk. */
const TURBO_WIN_SPEED = 4
/**
 * Tumble playback in turbo. Lower than the reels' 3.5: a cascade has to
 * stay legible — the player still needs to see WHICH symbols paid before
 * they blow up, and past 3× the highlight is gone before the eye lands.
 */
export const TURBO_TUMBLE_SPEED = 2.6

/**
 * Guard against a double start.
 *
 * In StrictMode React mounts effects twice, so boot() is called twice in a
 * row and without this would fire two identical requests in parallel. The
 * server tolerates that, but sending a duplicate is still pointless.
 */
let bootPromise: Promise<void> | null = null

/**
 * Getting the player authenticated, in order of preference:
 *
 *   1. a launch token in the URL — the operator has just opened the iframe;
 *   2. a refresh token from sessionStorage — the same session after a page
 *      reload (the launch token is single-use and cannot be replayed);
 *   3. a dev session — local development against AUTH_MODE=dev-header.
 *
 * Step 3 is not a fallback for a FAILED launch: if the operator sent us a
 * token and it did not work, silently logging in as the local test player
 * would hide a broken integration behind a working-looking game. It only
 * runs when there was no token to begin with.
 */
interface Authenticated {
  balance: BalanceResponse
  /** The game the operator bound the session to, if it bound one. */
  boundGameId: string | null
}

async function authenticate(): Promise<Authenticated> {
  const launchToken = takeLaunchTokenFromUrl()
  if (launchToken) {
    const pair = await api.exchangeLaunchToken(launchToken)
    return {
      balance: await api.balance(pair.gameId ?? undefined),
      boundGameId: pair.gameId,
    }
  }

  const resumed = await api.resumeSession()
  if (resumed) {
    // The binding has to survive a reload: the launch token is single-use,
    // so after F5 this resume is the only thing that still knows the player
    // was sent into one specific game.
    return {
      balance: await api.balance(resumed.gameId ?? undefined),
      boundGameId: resumed.gameId,
    }
  }

  // Already signed in this tab (the player just used the form): no need to
  // spend a refresh rotation to learn what we already hold.
  if (playerSession.isBearer() && !playerSession.needsRefresh()) {
    return { balance: await api.balance(), boundGameId: null }
  }

  // Nothing to go on. Whether that means "show a form" or "open a dev
  // session" is the server's answer, not a guess: in jwt mode a dev
  // session cannot play, and falling into one would show a broken game.
  const mode = await api.authMode()
  if (mode.playerLogin) throw new LoginRequired()

  return { balance: await api.devSession(config.devExternalId), boundGameId: null }
}

/**
 * Not an error condition — the expected outcome of arriving without any
 * credentials. Thrown so boot() has one place to decide what to paint,
 * rather than every caller of authenticate() checking a nullable result.
 */
class LoginRequired extends Error {
  constructor() {
    super('login required')
    this.name = 'LoginRequired'
  }
}

/** The player's sound choice outlives the tab: nobody wants to mute twice. */
const SOUND_STORAGE_KEY = 'music.enabled'

function storedSoundOn(): boolean {
  try {
    return window.localStorage.getItem(SOUND_STORAGE_KEY) !== 'off'
  } catch {
    // Private mode / blocked storage: default to sound on, same as a first visit.
    return true
  }
}

export const useGameStore = create<GameState>((set, get) => ({
  phase: 'boot',
  screen: 'lobby',
  error: null,
  authMethod: null,

  games: [],
  defaultGameId: '',
  game: null,
  theme: fallbackTheme([]),
  themes: {},
  selectedGameId: null,
  playerId: null,
  balanceCoins: 0,
  currency: 'UAH',
  allowedBets: [],
  betCoins: 0,
  mode: 'base',

  lastRound: null,
  displayWin: 0,
  freeSpinsLeft: 0,
  freeSpinIndex: 0,
  inFreeSpins: false,
  lastBombSum: 0,
  globalMult: -1,

  gambleOffer: null,
  lastGamble: null,
  gambleBusy: false,

  autoSpinsLeft: 0,
  startAutoSpins(count) {
    if (get().phase !== 'idle' || count <= 0) return
    // An open Risk Game offer is dropped: autospin never gambles. The next spin
    // closes the offer on the server anyway (lines contract §4).
    set({ autoSpinsLeft: count, gambleOffer: null, lastGamble: null })
    flushRoundEnd(get().currency)
  },
  stopAutoSpins() {
    set({ autoSpinsLeft: 0 })
  },

  celebrating: false,
  setCelebrating(on) {
    set({ celebrating: on })
  },

  turbo: false,
  toggleTurbo() {
    set({ turbo: !get().turbo })
  },

  boundGameId: null,
  framed: isFramed(),

  soundOn: storedSoundOn(),
  setSoundOn(on) {
    if (get().soundOn === on) return
    set({ soundOn: on })
    setSfxMuted(!on)
    try {
      window.localStorage.setItem(SOUND_STORAGE_KEY, on ? 'on' : 'off')
    } catch {
      // Nothing to do: the preference simply will not survive the tab.
    }
  },

  async refreshBalance() {
    // Deliberately unguarded by phase: the operator credits an account when
    // it credits it, and a balance that is one round stale is worse than a
    // balance that updates mid-animation — the round's own response
    // overwrites it at settle anyway.
    try {
      const balance = await api.balance(get().selectedGameId ?? undefined)
      set({ balanceCoins: balance.balanceCoins, currency: balance.currency })
    } catch (err) {
      // A failed refresh is not the player's problem: the balance they see
      // is still the last one the server confirmed. It IS the operator's
      // problem — they asked for this over postMessage and got silence.
      log.warn('balance.refresh-failed', 'the operator asked for a refresh and it did not land', {
        reason: err instanceof Error ? err.message : String(err),
      })
    }
  },

  /**
   * Signs a player in and starts the game.
   *
   * The error is deliberately not narrowed for display: the server answers
   * a wrong password and an unknown login identically, and so must this.
   */
  async signIn(login: string, password: string) {
    await api.playerLogin(login, password)
    playerSession.rememberPasswordLogin()
    // boot() picks up the session that playerLogin just adopted.
    await get().boot()
  },

  /**
   * Ends the session and returns to the form.
   *
   * A reload rather than a state reset: the store holds a game, a theme, a
   * round and a Pixi scene built for the player who is leaving, and
   * unpicking all of that by hand is how a stale balance ends up in front
   * of the next person to sign in.
   */
  async signOut() {
    await api.logout()
    window.location.reload()
  },

  async boot() {
    if (bootPromise) return bootPromise

    bootPromise = (async () => {
      set({ phase: 'boot', error: null })
      // Effects honour the stored choice from the first sound on, not from
      // the first time the toggle is clicked.
      setSfxMuted(!get().soundOn)
      startBridge({
        setMuted: (muted) => get().setSoundOn(!muted),
        refreshBalance: () => void get().refreshBalance(),
      })
      try {
        // We do not load a specific game's info here: the lobby is happy
        // with the cards, and the full info arrives on entering the demo.
        const [lobby, auth] = await Promise.all([api.games(), authenticate()])
        const session = auth.balance
        set({
          phase: 'idle',
          screen: 'lobby',
          // Read from storage rather than remembered in memory: a reload
          // resumes the session from the refresh token, and the control
          // must survive that.
          authMethod: playerSession.wasPasswordLogin() ? 'password' : null,
          games: lobby.games,
          defaultGameId: lobby.defaultGameId,
          boundGameId: auth.boundGameId,
          playerId: session.playerId,
          balanceCoins: session.balanceCoins,
          currency: session.currency,
          allowedBets: session.allowedBetsCoins,
          betCoins: session.defaultBetCoins,
          mode: 'base',
        })

        // A bound session never sees our lobby: the operator already picked
        // the game, and this happens here rather than in App so that the
        // list is not painted for a frame before being replaced.
        if (auth.boundGameId) await get().openGame(auth.boundGameId)

        emit({
          type: 'gameLoaded',
          gameId: auth.boundGameId,
          currency: session.currency,
          balanceCoins: session.balanceCoins,
        })
        // Only now: gameLoaded already carries the opening balance, and the
        // mirror would otherwise report the 0 → real-balance step of boot
        // itself as an update — arriving BEFORE the "hello", which is the
        // one ordering an integrator is entitled to rely on.
        mirrorToOperator()

        // Themes come after the lobby is already on screen: they are art,
        // and art must never delay the first paint. Previews start as
        // placeholders and swap to themed colours a moment later.
        const themes = await loadThemes(lobby.games)
        set({ themes })
      } catch (err) {
        // Arriving without credentials is not a failure: it is the first
        // screen. bootPromise is released so the form can boot again once
        // the player has signed in.
        if (err instanceof LoginRequired) {
          set({ phase: 'login', error: null })
          bootPromise = null
          return
        }
        // The blank-screen failure. Everything the player will report as
        // "it did not load" arrives here.
        log.error('boot.failed', err, 'the game could not start')
        set({ phase: 'error', error: describe(err) })
        bootPromise = null
      }
    })()

    return bootPromise
  },

  async openGame(gameId) {
    const { phase, games, selectedGameId } = get()
    // The game cannot change mid-round: the animation still owns the board.
    if (phase === 'spinning' || selectedGameId === gameId) return

    const summary = games.find((g) => g.gameId === gameId)
    if (!summary) {
      set({ error: `Game "${gameId}" is not in the lobby` })
      return
    }

    // The bets come from the card — they already account for this game's
    // reference bet, so an extra balance request is not needed.
    set({
      screen: 'game',
      selectedGameId: gameId,
      game: null,
      // Placeholder until the real theme resolves — the board is not on
      // screen yet anyway, and this keeps `theme` free of nulls.
      theme: fallbackTheme(summary.symbols),
      error: null,
      lastRound: null,
      displayWin: 0,
      freeSpinsLeft: 0,
      freeSpinIndex: 0,
      lastBombSum: 0,
      gambleOffer: null,
      lastGamble: null,
      gambleBusy: false,
      autoSpinsLeft: 0,
      celebrating: false,
      allowedBets: summary.allowedBetsCoins,
      betCoins: summary.defaultBetCoins,
      mode: 'base',
    })

    try {
      // The theme is fetched next to the game info, not after it: the board
      // mounts as soon as `game` is set, and mounting it twice — once bare,
      // once themed — would rebuild the WebGL context for nothing.
      const [info, theme] = await Promise.all([
        api.gameInfo(gameId),
        get().themes[gameId] ?? loadTheme(gameId, summary.symbols),
      ])
      // While the info was loading the player could have left for the lobby
      // or opened another game — then nobody needs this response any more.
      if (get().selectedGameId !== gameId) return
      set({ game: info, theme })
      // Not awaited: decoding a dozen files must not hold up the board.
      // Until it finishes the effects are simply silent, which is the same
      // state a theme without sounds is in.
      void loadSfx(gameId, theme.audio.sfx, theme.audio.sfxVolume)
    } catch (err) {
      if (get().selectedGameId !== gameId) return
      log.error('game.open-failed', err, 'the game info or its theme did not load', { gameId })
      set({ error: describe(err) })
    }
  },

  closeGame() {
    if (get().phase === 'spinning' || get().gambleBusy) return

    // Leaving with the Risk Game still on offer is the player declining it:
    // the win is already on the balance, so the round ends worth exactly what
    // the spin paid. Without this the lobby would wait for an end forever.
    flushRoundEnd(get().currency)

    // Bound session: there is no lobby of ours to go back to. Ask the
    // operator to close the iframe and stay exactly where we are — if the
    // lobby ignores the request, the player keeps a working game rather
    // than an empty screen.
    if (get().boundGameId) {
      emit({ type: 'exitRequest' })
      return
    }

    // The next game brings its own sounds; holding a dozen decoded buffers
    // for a skin that is no longer on screen is pure memory.
    unloadSfx()
    set({
      screen: 'lobby',
      selectedGameId: null,
      game: null,
      error: null,
      displayWin: 0,
      freeSpinsLeft: 0,
      freeSpinIndex: 0,
      lastBombSum: 0,
      gambleOffer: null,
      lastGamble: null,
      autoSpinsLeft: 0,
    })
  },

  setBet(betCoins) {
    const { allowedBets, phase } = get()
    if (phase !== 'idle' || !allowedBets.includes(betCoins)) return
    set({ betCoins })
  },

  setMode(mode) {
    const { game, phase } = get()
    if (phase !== 'idle' || !game?.modes.includes(mode)) return
    set({ mode })
  },

  chargeFor(mode) {
    const { game, betCoins } = get()
    const m = mode ?? get().mode
    if (!game) return betCoins
    if (m === 'ante') return betCoins * (game.anteBet?.costMultiplier ?? 1)
    if (m === 'buy') return betCoins * (game.buyFeature?.costMultiplier ?? 1)
    return betCoins
  },

  async spin(animators) {
    const { phase, playerId, betCoins, balanceCoins, game, selectedGameId, gambleBusy } = get()
    if (phase !== 'idle' || gambleBusy || !playerId || !game || !selectedGameId) return

    // Buy is a one-shot action: the mode itself does not "stick" after the round.
    const mode = get().mode
    const charge = get().chargeFor(mode)
    if (balanceCoins < charge) {
      set({ error: 'Insufficient balance', autoSpinsLeft: 0 })
      return
    }

    // The mechanics sit below the win sounds on purpose: they play every
    // round, the wins are the news.
    playSfx('spin-start', 0.6, nextSpinPitch())

    set({
      phase: 'spinning',
      error: null,
      displayWin: 0,
      freeSpinsLeft: 0,
      freeSpinIndex: 0,
      lastBombSum: 0,
      globalMult: -1,
      // A new spin closes any open gamble on the server too (contract §4).
      gambleOffer: null,
      lastGamble: null,
    })

    // Whatever the previous round was still holding back: this spin closes
    // its gamble on the server too, so that round is worth what was on the
    // table when the player walked away from the offer.
    flushRoundEnd(get().currency)

    // roundStart/roundEnd must come in pairs — the operator's lobby locks
    // its chrome between them, and a start with no end leaves it locked for
    // the session. This flag is what the catch below uses to close the pair.
    let started = false

    try {
      const round = await api.spin(betCoins, mode, selectedGameId)

      started = true
      emit({ type: 'roundStart', gameId: selectedGameId, betCoins: round.chargedCoins })

      // Show the charge right away, before the animation, so the balance
      // does not "jump" later.
      set({ balanceCoins: balanceCoins - round.chargedCoins, lastRound: round })

      // Pauses between spins are theme timings too: they are part of how a
      // round feels, and they belong next to the animation timings rather
      // than in a constant the designer cannot reach.
      const theme = get().theme
      if (round.mechanic === 'ways' && round.spins && animators.ways) {
        await playWays(round, animators.ways, set, theme, () => get().turbo)
      } else if (round.mechanic === 'lines' && round.spins && animators.ways) {
        await playLines(
          round,
          animators.ways,
          animators.lines,
          set,
          get().turbo,
          new Set(game.symbols.filter((s) => s.kind === 'wild').map((s) => s.code)),
        )
      } else if (round.mechanic === 'tumble' && round.result && animators.tumble) {
        await playTumble(
          round,
          animators.tumble,
          animators.banner,
          set,
          game.freeSpins.retrigger,
          theme,
          () => get().turbo,
        )
      }

      // The Risk Game offer is part of the response; absent for non-lines
      // games. Never offer it mid-autospin: the panel would block the loop
      // and asking the player to decide contradicts "spin automatically".
      // The offer comes back on the last round, when autoSpinsLeft is 0.
      const offer = get().autoSpinsLeft > 0 ? null : (round.gamble ?? null)

      // The final balance comes from the response, not from what we
      // accumulated: the server may have trimmed the win by the maxWin cap.
      set({
        phase: 'idle',
        balanceCoins: round.balanceCoins,
        displayWin: round.winCoins,
        freeSpinsLeft: 0,
        freeSpinIndex: 0,
        globalMult: -1,
        // buy does not stay switched on
        mode: mode === 'buy' ? 'base' : mode,
        gambleOffer: offer,
      })

      // An open Risk Game means the round is not settled: the player can
      // still double the win or lose it, and roundEnd promises the operator
      // a final balance. So it waits for the offer to close.
      if (offer?.available) heldRoundEnd = { round, gameId: selectedGameId }
      else emitRoundEnd(round, selectedGameId, get().currency)
    } catch (err) {
      // The money path. `started` says which side of the charge this
      // happened on, and that is the first question anyone investigating a
      // disputed round will ask: before it, nothing was taken; after it, the
      // server has a settled round the player may not have seen play out.
      log.error('spin.failed', err, started ? 'after the charge' : 'before the charge', {
        gameId: selectedGameId,
        betCoins,
        mode,
        charged: started,
        roundId: get().lastRound?.roundId,
      })

      // A failed request also stops the autospin: retrying it blind would
      // just repeat the same error `autoSpinsLeft` more times.
      set({
        phase: 'idle',
        error: describe(err),
        freeSpinsLeft: 0,
        freeSpinIndex: 0,
        globalMult: -1,
        autoSpinsLeft: 0,
      })

      // The round was charged and then the animation blew up. The money is
      // settled on the server either way, so report the response we have
      // rather than leaving the lobby waiting for an end that never comes.
      const round = get().lastRound
      // Nothing is held for this round yet, but a hold from an earlier one
      // must not outlive the round it belongs to.
      heldRoundEnd = null
      if (started && round) emitRoundEnd(round, selectedGameId, get().currency)
    }
  },

  gambleDouble(guess) {
    return gambleStep(get, set, 'double', guess)
  },

  gambleCollect() {
    return gambleStep(get, set, 'collect')
  },
}))

/**
 * A spin that has played out but whose `roundEnd` is held back because the
 * Risk Game is still open.
 *
 * Only a lines game can get here. The operator's lobby reads `roundEnd` as
 * "settled, balance final", and a win the player can still double or lose is
 * neither — reporting it early would put a number in the operator's round
 * history that the player never actually walked away with.
 */
let heldRoundEnd: { round: SpinResponse; gameId: string } | null = null

/** The settled round, in the shape the operator's lobby consumes. */
function emitRoundEnd(
  round: SpinResponse,
  gameId: string,
  currency: string,
  // What the round finally paid, once the Risk Game had its say. Absent when
  // there was no gamble: then the spin's own numbers are the final ones.
  settled?: { winCoins: number; balanceCoins: number },
): void {
  emit({
    type: 'roundEnd',
    round: {
      roundId: round.roundId,
      gameId,
      betCoins: round.chargedCoins,
      winCoins: settled ? settled.winCoins : round.winCoins,
      balanceCoins: settled ? settled.balanceCoins : round.balanceCoins,
      currency,
      capped: round.capped,
    },
  })
}

/**
 * Closes a held round, if there is one. Safe to call when there is not.
 *
 * Every way out of an open offer goes through here — the step that closes it,
 * the next spin, autospin dropping it — because the one path that forgets is
 * the one that leaves the operator's lobby locked for the rest of the session.
 */
function flushRoundEnd(currency: string, settled?: { winCoins: number; balanceCoins: number }): void {
  const held = heldRoundEnd
  if (!held) return
  heldRoundEnd = null
  emitRoundEnd(held.round, held.gameId, currency, settled)
}

/**
 * Starts mirroring the balance and errors to the operator over postMessage.
 *
 * A subscription rather than an `emit` next to every `set`: the balance is
 * written from the spin, the gamble and the refresh, and the one that gets
 * forgotten during a later change is exactly the one the operator's wallet
 * display would then quietly desynchronise on.
 *
 * Called once, from boot, right after `gameLoaded`.
 */
let mirroring = false

function mirrorToOperator(): void {
  // boot() runs again after a failed boot, and a second subscription would
  // duplicate every event for the rest of the session.
  if (mirroring) return
  mirroring = true

  useGameStore.subscribe((state, prev) => {
    if (state.balanceCoins !== prev.balanceCoins) {
      emit({
        type: 'balanceUpdate',
        balanceCoins: state.balanceCoins,
        currency: state.currency,
      })
    }
    // Only a new message: the same error re-set by a retry is not news, and
    // clearing it is the client's own bookkeeping.
    if (state.error && state.error !== prev.error) {
      emit({ type: 'error', message: state.error })
    }
  })
}

/**
 * One Risk Game request. The result is applied verbatim: the balance, the
 * next offer and the card all come from the server. On failure the offer is
 * left as it was — the player can retry or collect.
 */
async function gambleStep(
  get: () => GameState,
  set: Set,
  action: 'double' | 'collect',
  guess?: GambleGuess,
): Promise<void> {
  const { phase, playerId, lastRound, gambleOffer, gambleBusy } = get()
  if (phase !== 'idle' || gambleBusy || !playerId || !lastRound || !gambleOffer?.available) return
  if (action === 'double' && !guess) return

  set({ gambleBusy: true, error: null })
  // The flip sound belongs to the request, not the response: the card
  // starts turning as soon as the player commits.
  if (action === 'double') playSfx('gamble-flip')
  try {
    const res = await api.gamble(lastRound.roundId, action, guess)
    if (action === 'double') {
      // Scheduled rather than awaited: the panel must update the moment
      // the response lands, but the verdict has to arrive after the card
      // has turned, not on top of the flip.
      const outcome = res.payoutCoins > 0 ? 'gamble-win' : 'gamble-lose'
      setTimeout(() => playSfx(outcome), GAMBLE_REVEAL_MS)
    }
    set({
      gambleBusy: false,
      lastGamble: res,
      gambleOffer: res.gamble,
      balanceCoins: res.balanceCoins,
      // What the round is worth now: the payout of the step just made
      // (0 after a loss); collect keeps whatever was on the table.
      displayWin: action === 'double' ? res.payoutCoins : gambleOffer.stakeCoins,
    })

    // Collected, lost, or out of steps: the round is finally worth what the
    // player is left holding, and that is the number the operator gets.
    if (!res.gamble?.available) {
      flushRoundEnd(get().currency, {
        winCoins: action === 'double' ? res.payoutCoins : gambleOffer.stakeCoins,
        balanceCoins: res.balanceCoins,
      })
    }
  } catch (err) {
    // Also money: the stake is on the table and the step did not resolve.
    log.error('gamble.failed', err, `the ${action} step did not resolve`, {
      action,
      roundId: lastRound.roundId,
      stakeCoins: gambleOffer.stakeCoins,
    })
    set({ gambleBusy: false, error: describe(err) })
  }
}

type Set = (partial: Partial<GameState>) => void

async function playWays(
  round: SpinResponse,
  animate: WaysAnimator,
  set: Set,
  theme: ResolvedTheme,
  turbo: () => boolean,
): Promise<void> {
  // The same omission tumble had: the reels take a playback rate, but
  // nothing was passing one, so turbo did nothing on a ways game either.
  // Read per spin, so a free-spin run picks the toggle up as it goes.
  const speed = (): number => (turbo() ? TURBO_SPIN_SPEED : 1)
  const spins = round.spins ?? []
  let shown = 0
  for (let i = 0; i < spins.length; i++) {
    const spin = spins[i]
    if (spin.kind === 'free') {
      set({ freeSpinsLeft: spins.length - i, freeSpinIndex: i })
      await sleep(theme.timings.ways.betweenFreeSpins / speed())
    }
    await animate(spin.window, { speed: speed() })
    shown += spin.winCoins
    set({ displayWin: shown })
  }
}

/**
 * Lines: one spin — reels stop on the window, then the winning lines the
 * server listed are lit one by one. The counter shows the spin's total once
 * the reels stop; the line overlay is decoration for a number already known.
 */
async function playLines(
  round: SpinResponse,
  spinReels: WaysAnimator,
  showLines: LinesAnimator | undefined,
  set: Set,
  turbo: boolean,
  wildCodes: ReadonlySet<string>,
): Promise<void> {
  const spin = round.spins?.[0]
  if (!spin) return
  // The reels always scroll — that motion IS the slot. The ONLY thing that
  // changes the rate is turbo, which is the player's own setting.
  //
  // The rate must never depend on the outcome. It used to: a losing round
  // scrolled at 2.2× "because it has nothing to show off". Two things were
  // wrong with that. It telegraphed the result — the reels were already
  // racing before they stopped, so the spin had no suspense left to
  // deliver. And because most rounds lose, the fast rate was what a player
  // saw most of the time, which reads as turbo being stuck on. It also
  // pushed the round cycle the wrong way against S12 (game speed).
  const speed = turbo ? TURBO_SPIN_SPEED : 1
  // The spin has a bed: a looping ticker under the scrolling reels, faded
  // out when the last reel lands. The stagger of the reel-stop clunks then
  // plays against silence, which is what makes them land.
  const stopRoll = startSfxLoop('reel-spin', 0.5)
  try {
    await spinReels(spin.window, { speed })
  } finally {
    stopRoll()
  }
  set({ displayWin: spin.winCoins })
  const lineWins = spin.lineWins ?? []
  // Per-combination chimes come from the line reveal itself (PixiLines);
  // here only the round-level accents live.
  if (round.winCoins > 0) {
    if (isBigWin(round)) playSfx('win-big')
    // A wild that actually took part in a paying line gets its own accent
    // under the win chime. Wilds sitting idle off the paid lines do not:
    // the sound marks a substitution, not the symbol being on screen.
    if (wildCodes.size > 0 && paysWithWild(spin.window, lineWins, wildCodes)) {
      playSfx('wild-hit', 0.7)
    }
  }
  // In turbo the win still lights up, once and briefly: the player has to
  // see WHICH symbols paid, even when they do not want the ceremony.
  if (showLines && lineWins.length > 0) {
    await showLines(lineWins, turbo ? { speed: TURBO_WIN_SPEED, cycle: false } : undefined)
  }
}

/**
 * Tumble: every chain → an animation; the win counter grows frame by frame.
 * All contract amounts are in reference coins, so multiply by betMultiplier.
 */
async function playTumble(
  round: SpinResponse,
  animate: TumbleAnimator,
  banner: BannerFn | undefined,
  set: Set,
  retrigger: { minScatters: number; spins: number } | null,
  theme: ResolvedTheme,
  turbo: () => boolean,
): Promise<void> {
  const T = theme.timings.tumble
  // A tumble round is animation plus the waits BETWEEN animations, and over
  // ten free spins the waits are the longer half. Speeding up only the board
  // would leave every pause at full length, which is why the toggle read as
  // doing nothing at all.
  //
  // Read per step rather than captured: a bonus is ONE round that runs for
  // the better part of a minute, so a rate frozen at round start left the
  // player who reached for turbo mid-bonus — which is exactly when they
  // reach for it — watching the slow one to the end.
  const speed = (): number => (turbo() ? TURBO_TUMBLE_SPEED : 1)
  const wait = (ms: number): Promise<void> => sleep(ms / speed())
  const result = round.result!
  const k = round.betMultiplier
  const spins = result.trace
  const freeCount = spins.filter((s) => s.kind === 'free').length
  // The "left" counter must not spoil retriggers: it starts from the
  // initial award and only grows once a retrigger is already shown on
  // screen.
  const retriggerSpins = retrigger?.spins ?? 0
  const initialAward = freeCount - result.fsRetriggers * retriggerSpins
  let awarded = initialAward
  let settled = 0 // total of finished chains, in the player's coins
  let fsSettled = 0
  let freeIdx = 0
  /**
   * Whether anything has already announced the bonus. The banner carries the
   * fanfare, so the sound must not fire twice when there is one — and must
   * fire when there is not.
   */
  let announced = false
  const show = async (text: string, kind: 'free' | 'win' | 'bomb'): Promise<void> => {
    if (banner) await banner(text, kind, { speed: speed() })
    else await wait(T.freeSpinsIntroMs)
  }

  for (let i = 0; i < spins.length; i++) {
    const spin = spins[i]

    // Persistent games carry the accumulator on every spin: show the counter
    // from the first free spin (starting at ×0), update it as each free spin
    // settles. Non-persistent games never set it (stays -1 → hidden).
    const persistent = spin.globalMultAfter !== undefined
    if (spin.kind === 'free') {
      freeIdx++
      // A BOUGHT bonus has no trigger banner (the player chose it), so the
      // fanfare that normally comes with the banner would be skipped and
      // the round would start cold. Fire it here instead, right as the
      // music crossfades up — the two together are the entrance.
      //
      // The test is "has anything announced this yet", NOT `fsTriggered`.
      // That flag is defined as `fsSpinsPlayed > 0` (see the contract), so
      // it is TRUE for a bought bonus as well — and since a buy trace also
      // carries no base spin, the banner branch below never ran either.
      // Between them a bought bonus started in complete silence.
      if (freeIdx === 1 && !announced) playSfx('free-intro', 0.8)
      set({
        freeSpinsLeft: awarded - freeIdx + 1,
        freeSpinIndex: freeIdx,
        // For a triggered bonus the crossfade already started with the
        // banner; this is the entry point for the BOUGHT path, where there
        // is no banner to start it.
        inFreeSpins: true,
        lastBombSum: 0,
        ...(persistent && freeIdx === 1 ? { globalMult: 0 } : {}),
      })
      await wait(T.betweenSpinsMs)
    }

    await animate(
      spin,
      {
        onFrameWin: (refCoins) => set({ displayWin: settled + refCoins * k }),
        onBombs: (mult, chainTotal) => set({ lastBombSum: mult, displayWin: settled + chainTotal * k }),
      },
      { speed: speed() },
    )

    if (persistent && spin.kind === 'free') set({ globalMult: spin.globalMultAfter ?? 0 })

    settled += spin.totalWin * k
    if (spin.kind === 'free') fsSettled += spin.totalWin * k
    set({ displayWin: settled })

    // Trigger from a base spin (for buy there is no banner — the player
    // bought it themselves)
    if (spin.kind === 'base' && result.fsTriggered) {
      // The crossfade starts WITH the banner: fanfare on top of the base
      // loop fading out, and by the time the first free spin rolls the
      // livelier track has fully arrived — fanfare first, then the tempo.
      set({ inFreeSpins: true })
      announced = true
      await show(`+${initialAward} FREE SPINS`, 'free')
    }
    if (spin.kind === 'free' && retrigger && spin.scatterCount >= retrigger.minScatters) {
      awarded += retriggerSpins
      set({ freeSpinsLeft: awarded - freeIdx })
      await show(`+${retriggerSpins} FREE SPINS`, 'free')
    }
  }

  if (freeCount > 0) {
    // Back to the base mood as the summary appears: the banner IS the
    // scene change, and music that lingers past it contradicts the screen.
    set({ inFreeSpins: false })
    await show(`FREE SPINS: ${fsSettled.toLocaleString('en-US')}`, fsSettled > 0 ? 'win' : 'free')
  }
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error'
}
